import { Theme } from '@logto/schemas';
import type { Nullable } from '@silverhand/essentials';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import { useContext, useState } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import ConnectIcon from '@/assets/icons/connect-icon.svg?react';
import PharmacyLogo from '@/assets/pharmacy-logo.svg?react';
import ThirdPartyAppIconDark from '@/assets/icons/third-party-app-dark.svg?react';
import ThirdPartyAppIcon from '@/assets/icons/third-party-app.svg?react';
import ArrowPrev from '@/shared/assets/icons/arrow-prev.svg?react';
import DynamicT from '@/shared/components/DynamicT';

import styles from './index.module.scss';

export type Props = {
  readonly className?: string;
  readonly logo?: Nullable<string>;
  readonly thirdPartyLogo?: Nullable<string>;
  readonly headline?: TFuncKey;
  readonly headlineInterpolation?: Record<string, unknown>;
};

const BrandingHeader = ({
  thirdPartyLogo,
  headline,
  headlineInterpolation,
  className,
}: Props) => {
  const { theme } = useContext(PageContext);
  /**
   * The third-party logo is a remote asset the client declares (e.g. a CIMD client's `logo_uri`),
   * loaded with no user interaction: `no-referrer` keeps it from carrying the tenant origin to
   * that host, and a load failure falls back to the third-party application icon so the header
   * keeps its shape.
   */
  const [isThirdPartyLogoBroken, setIsThirdPartyLogoBroken] = useState(false);
  const FallbackThirdPartyLogo = theme === Theme.Light ? ThirdPartyAppIcon : ThirdPartyAppIconDark;

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.logoWrapper}>
        <button type="button" className={styles.back} aria-label="Назад" title="Назад" onClick={() => window.history.back()}>
          <ArrowPrev aria-hidden="true" focusable="false" />
        </button>
          {thirdPartyLogo &&
            (isThirdPartyLogoBroken ? (
              <FallbackThirdPartyLogo className={classNames(styles.logo, styles.thirdPartyLogo)} />
            ) : (
              <img
                className={classNames(styles.logo, styles.thirdPartyLogo)}
                alt="third party logo"
                src={thirdPartyLogo}
                referrerPolicy="no-referrer"
                onError={() => {
                  setIsThirdPartyLogoBroken(true);
                }}
              />
            ))}
          {thirdPartyLogo && <ConnectIcon className={styles.connectIcon} />}
          <button type="button" className={styles.brand} aria-label="Назад — Здоровье" onClick={() => window.history.back()}>
            <PharmacyLogo aria-hidden="true" />
            <span>Здоровье</span>
          </button>
      </div>

      {headline && (
        <div className={styles.headline}>
          <DynamicT forKey={headline} interpolation={headlineInterpolation} />
        </div>
      )}
    </div>
  );
};

export default BrandingHeader;
